import { useState } from "react";
import { Menu, Podcast, LogOut, Settings, UserCircle, Search, LayoutDashboard, Shield, CreditCard, Undo2 } from "lucide-react";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { BrandLogo } from "@/components/marketing/BrandLogo";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TooltipMenuControls } from "./TooltipMenuControls";
import { useAuth } from "@/contexts/AuthContext";
import { GlobalSearch } from "@/components/layout/GlobalSearch";
import { useAdminPanelLink } from "@/hooks/useAdminPanelLink";

interface PodcasterHeaderProps {
  onMenuClick?: () => void;
}

export function PodcasterHeader({ onMenuClick }: PodcasterHeaderProps) {
  const { user, profile, signOut, isImpersonating, switchBack } = useAuth();
  const { showAdminPanel, adminPanelTo } = useAdminPanelLink();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  // Get initials from display name or email
  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  // Get display name or email
  const getDisplayName = () => {
    return profile?.display_name || user?.email?.split("@")[0] || "User";
  };

  return (
    <header className="sticky top-0 z-50 flex h-16 items-center bg-sidebar px-4 sm:px-6 border-b border-sidebar-foreground/10">
      {/* Left side - Hamburger (mobile/tablet) + Logo */}
      <div className="flex items-center gap-3">
        {/* Hamburger menu - visible below 1200px */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="md:hidden text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Logo - always visible */}
        <Link to="/dashboard" className="flex items-center gap-2" aria-label="PODGO dashboard">
          <BrandLogo className="h-8 w-auto" decorative>
            <>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
                <Podcast className="h-4 w-4 text-accent-foreground" />
              </div>
              <span className="text-lg font-semibold tracking-tight text-sidebar-foreground">
                PODGO
              </span>
            </>
          </BrandLogo>
        </Link>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 ml-auto text-sidebar-foreground">
        {/* Search */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-sidebar-foreground hover:bg-sidebar-accent"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="h-5 w-5" />
          <span className="sr-only">Search</span>
        </Button>
        
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        {/* Notifications */}
        <NotificationCenter />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="relative h-9 w-9 rounded-full hover:bg-sidebar-accent"
            >
              <Avatar className="h-9 w-9 ring-2 ring-sidebar-foreground/20">
                {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />}
                <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{getDisplayName()}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {showAdminPanel ? (
              <>
                <DropdownMenuItem onClick={() => navigate(adminPanelTo)}>
                  <Shield className="mr-2 h-4 w-4" aria-hidden="true" />
                  Admin Panel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                  Podcaster Portal
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : (
              <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                Dashboard
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/settings")}>

              <UserCircle className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Account Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/billing")}>
              <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
              Billing
            </DropdownMenuItem>
            <TooltipMenuControls />
            <DropdownMenuSeparator />
            {isImpersonating && (
              <>
                <DropdownMenuItem onClick={switchBack}>
                  <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Switch Back
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
